/**
 * GameLoop — pure TypeScript time loop, no Phaser dependency.
 *
 * Replaces TimeSystem. Uses setInterval to tick, reads SECONDS_PER_DAY
 * from game config, and emits the same events as TimeSystem did:
 * DAY_CHANGED, WEEK_CHANGED, MONTH_CHANGED.
 */

import { EventBus, GameEvents } from '@game/utils/EventBus'
import { useGameStore } from '@store/gameStore'
import { GAME_CONFIG } from '@/config/gameConfig'

// Interval between ticks in ms (fine-grained for smooth speed changes)
const TICK_MS = 100

export class GameLoop {
  private static instance: GameLoop | null = null

  private intervalId: ReturnType<typeof setInterval> | null = null
  private accumulated: number = 0
  private lastWeek: number = 0
  private lastMonth: number = 0

  private constructor() {}

  static getInstance(): GameLoop {
    if (!GameLoop.instance) {
      GameLoop.instance = new GameLoop()
    }
    return GameLoop.instance
  }

  start(): void {
    if (this.intervalId !== null) return // already running

    const store = useGameStore.getState()
    this.lastWeek = store.week
    this.lastMonth = store.month
    this.accumulated = 0

    this.intervalId = setInterval(() => this.tick(), TICK_MS)
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  private tick(): void {
    const store = useGameStore.getState()
    if (store.isPaused) return

    const msPerDay = (GAME_CONFIG.SECONDS_PER_DAY * 1000) / store.gameSpeed
    this.accumulated += TICK_MS

    while (this.accumulated >= msPerDay) {
      this.accumulated -= msPerDay
      this.advanceDay()
    }
  }

  private advanceDay(): void {
    const store = useGameStore.getState()
    store.advanceDay()

    const next = useGameStore.getState()

    EventBus.emit(GameEvents.DAY_CHANGED, {
      day: next.day,
      week: next.week,
      month: next.month,
    })

    if (next.week !== this.lastWeek) {
      this.lastWeek = next.week
      EventBus.emit(GameEvents.WEEK_CHANGED, {
        week: next.week,
        month: next.month,
      })
    }

    if (next.month !== this.lastMonth) {
      this.lastMonth = next.month
      EventBus.emit(GameEvents.MONTH_CHANGED, { month: next.month })
    }
  }
}
